import React from 'react';
import {
  Box, IconButton, Typography, useMediaQuery, useTheme, Divider,
  Checkbox, FormControlLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { InvTextField, InvSelect } from '../InvField';
import InvItemAutocomplete from '../InvItemAutocomplete';
import PoItemAutocomplete from '../PoItemAutocomplete';
import { formatCurrency, formatQty, lineTotal, computeLineDiscountAmount } from '../../utils';
import {
  minGrnExpiryDate, isPoRateOverridden, grnLineReceivesInBase, resolveLinePackOverride,
} from '../../utils/grnEntity';
import {
  parseConversionFactor, formatConversionFactor, composePurchaseToBase,
} from '../../utils/uomConversionEntity';
import { IPD_COLORS, cardSx } from '../../theme';

const FIELD_MIN_H = 40;

const actionColSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  alignSelf: 'center',
};

/** Below this, base_uom_price() quantizes a per-base price to nothing. */
const MIN_POSTABLE_BASE_PRICE = 0.0001;

/** One price restated per dispense unit, and per base unit when they differ. */
function packPriceLine(label, amount, packFactor, composed, issueUnit, baseUnit) {
  const value = Number(amount);
  if (!Number.isFinite(value) || value <= 0 || !packFactor) return null;
  const parts = [`${formatCurrency(value / packFactor)}/${issueUnit}`];
  if (composed && composed !== packFactor) {
    parts.push(`${formatCurrency(value / composed)}/${baseUnit}`);
  }
  return `${label} ${parts.join(' · ')}`;
}

function focusGrnLineItemField(lineNumber) {
  setTimeout(() => {
    const root = document.querySelector(`[data-grn-line="${lineNumber}"]`);
    if (!root) return;
    const input = root.querySelector('[data-grn-focus="item"] input');
    if (input) {
      input.focus();
      return;
    }
    root.querySelector('[data-grn-focus="item"] [role="combobox"]')?.focus();
  }, 80);
}

function handleDiscPercentKeyDown(e, index, onAdd) {
  if (e.key !== 'Tab' || e.shiftKey) return;
  e.preventDefault();
  onAdd(index);
  focusGrnLineItemField(index + 2);
}

function isLineExpiryPast(line, todayDate) {
  return Boolean(line.expiry_date && line.expiry_date <= todayDate);
}

function ExpiredOverrideControl({ line, todayDate, onToggle }) {
  if (!isLineExpiryPast(line, todayDate)) return null;
  return (
    <FormControlLabel
      control={(
        <Checkbox
          size="small"
          checked={Boolean(line.allow_expired_override)}
          onChange={e => onToggle(e.target.checked)}
        />
      )}
      label={(
        <Typography sx={{ fontSize: 11, color: IPD_COLORS.warning.text, lineHeight: 1.3 }}>
          Accept expired stock (override)
        </Typography>
      )}
      sx={{ mt: 0.5, ml: 0, alignItems: 'flex-start' }}
    />
  );
}

const readOnlyBoxSx = {
  minHeight: FIELD_MIN_H,
  px: 1.25,
  py: 0.75,
  borderRadius: 1,
  border: `1px solid ${IPD_COLORS.border}`,
  backgroundColor: IPD_COLORS.selectedBg || '#f8fafb',
  display: 'flex',
  alignItems: 'center',
};

const grid12Sx = {
  display: 'grid',
  gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
  gap: 1.5,
  alignItems: 'start',
};

function FieldLabel({ children, required }) {
  return (
    <Typography
      component="label"
      sx={{
        display: 'block',
        color: IPD_COLORS.textSecondary,
        fontSize: 11,
        fontWeight: 500,
        mb: 0.5,
        letterSpacing: 0.2,
      }}
    >
      {children}
      {required ? ' *' : null}
    </Typography>
  );
}

function ReadOnlyValue({ value, align = 'left', warn = false, mono = false }) {
  return (
    <Box sx={readOnlyBoxSx}>
      <Typography sx={{
        width: '100%',
        fontSize: 13,
        fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : 'inherit',
        textAlign: align,
        color: warn ? IPD_COLORS.error.text : IPD_COLORS.textPrimary,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>
        {value}
      </Typography>
    </Box>
  );
}

/**
 * The unit this line is received in. Read-only in both modes.
 *
 * Direct GRN takes the item master's purchase UOM; a PO-based line inherits the
 * PO's. Neither is picked here any more: changing the receiving unit at GRN time
 * changed what every quantity and price on the line meant, and the pack size
 * below is the supported way to say this delivery arrived packed differently.
 */
/** Direct GRN: the purchase unit, pickable from the units the item master can
 *  actually convert to base — anything else would fail at post-to-stock.
 *  PO-based GRN: read-only, inherited from the PO line. */
function UomCell({ isDirect, uomId, uomLabel, uomOptions, onChange }) {
  if (!isDirect) return <ReadOnlyValue value={uomLabel || '—'} />;
  return (
    <InvSelect
      label=""
      value={uomId}
      onChange={e => onChange(e.target.value)}
      options={uomOptions}
    />
  );
}

/**
 * This delivery's pack size, on the line rather than behind a button.
 *
 * The number is typed against the unit the item is DISPENSED in — "1 BOX = 10
 * STRIP" — because that is what the carton says and what the pharmacist counts
 * in. What actually posts is that number bridged to the item's base unit, so
 * the base equivalent is spelled out whenever the two are not the same thing.
 *
 * Every price on the line is quoted per receiving unit and gets divided by this
 * factor at posting. That is why the restated prices sit directly underneath: a
 * pack size wrong by a factor of ten is invisible in "1200.00" and obvious in
 * "10.00/TAB".
 */
function PackSizeCell({
  line, uomLabel, issueUomLabel, baseUomLabel, packErr, packReasonErr, handleField, gridColumn,
}) {
  const baseUnit = baseUomLabel || 'base unit';

  if (grnLineReceivesInBase(line)) {
    return (
      <Box sx={{ gridColumn }}>
        <FieldLabel>Pack Size</FieldLabel>
        <ReadOnlyValue value={`Received in ${baseUnit} — no pack`} />
      </Box>
    );
  }

  const unit = uomLabel || 'unit';
  const issueUnit = issueUomLabel || baseUnit;
  const packFactor = parseConversionFactor(line.pack_to_issue);
  const issueToBase = parseConversionFactor(line.issue_to_base_factor) ?? 1;
  const composed = packFactor != null ? composePurchaseToBase(packFactor, issueToBase) : null;
  const isOverride = resolveLinePackOverride(line) != null;
  const master = line.pack_to_issue_default;

  const priceLines = [
    packPriceLine('Cost', line.unit_rate, packFactor, composed, issueUnit, baseUnit),
    packPriceLine('MRP', line.mrp, packFactor, composed, issueUnit, baseUnit),
    packPriceLine('Sale', line.sale_rate, packFactor, composed, issueUnit, baseUnit),
  ].filter(Boolean);

  const roundsToNothing = composed != null
    && [line.unit_rate, line.mrp, line.sale_rate]
      .some(v => Number(v) > 0 && Number(v) / composed < MIN_POSTABLE_BASE_PRICE);

  return (
    <Box sx={{ gridColumn }}>
      <FieldLabel required={!master}>Pack Size</FieldLabel>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Typography sx={{ fontSize: 12, whiteSpace: 'nowrap', color: IPD_COLORS.textSecondary }}>
          {`1 ${unit} =`}
        </Typography>
        <InvTextField
          value={line.pack_to_issue || ''}
          onChange={e => handleField('pack_to_issue', e.target.value)}
          type="number"
          inputProps={{ min: 0, step: 'any' }}
          error={Boolean(packErr)}
        />
        <Typography sx={{ fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
          {issueUnit}
        </Typography>
        {composed != null && composed !== packFactor ? (
          <Typography sx={{ fontSize: 11, whiteSpace: 'nowrap', color: IPD_COLORS.textSecondary }}>
            {`= ${formatConversionFactor(composed)} ${baseUnit}`}
          </Typography>
        ) : null}
      </Box>

      <Typography sx={{
        fontSize: 11,
        mt: 0.5,
        color: packErr ? IPD_COLORS.error.text : IPD_COLORS.textSecondary,
      }}>
        {packErr || (master
          ? `Item master: ${master}${isOverride ? ' · changed for this GRN' : ''}`
          : 'No item-master rule — this GRN sets the pack size')}
      </Typography>

      {priceLines.length ? (
        <Typography sx={{
          fontSize: 11, color: IPD_COLORS.textSecondary, mt: 0.25, lineHeight: 1.6,
        }}>
          {priceLines.join('   ')}
        </Typography>
      ) : null}

      {roundsToNothing ? (
        <Typography sx={{ fontSize: 11, color: IPD_COLORS.warning.text, mt: 0.25 }}>
          {`A price this small per ${baseUnit} rounds to zero when posted — check the pack size.`}
        </Typography>
      ) : null}

      {isOverride ? (
        <Box sx={{ mt: 1 }}>
          <FieldLabel required>Reason</FieldLabel>
          <InvTextField
            value={line.override_reason || ''}
            onChange={e => handleField('override_reason', e.target.value)}
            error={Boolean(packReasonErr)}
            helperText={packReasonErr || 'Why this delivery differs from the item master'}
          />
        </Box>
      ) : null}
    </Box>
  );
}

function SectionTitle({ children }) {
  return (
    <Typography
      variant="overline"
      sx={{
        display: 'block',
        color: IPD_COLORS.textSecondary,
        fontSize: 10,
        letterSpacing: 0.8,
        mb: 1,
        mt: 0.5,
      }}
    >
      {children}
    </Typography>
  );
}

function DeleteLineButton({ canRemove, onRemove, index }) {
  return (
    <IconButton
      size="small"
      onClick={() => onRemove(index)}
      disabled={!canRemove}
      sx={{ color: IPD_COLORS.error.text }}
      aria-label={`Remove line ${index + 1}`}
    >
      <DeleteIcon fontSize="small" />
    </IconButton>
  );
}

function AddLineButton({ onAdd, index }) {
  return (
    <IconButton
      size="small"
      onClick={() => onAdd(index)}
      sx={{ color: IPD_COLORS.primary }}
      aria-label={`Add row below line ${index + 1}`}
      title="Add row below (Tab from Disc % also adds)"
    >
      <AddIcon fontSize="small" />
    </IconButton>
  );
}

function DiscPercentField({
  value, onChange, onAdd, index, disabled, error, helperText,
}) {
  return (
    <InvTextField
      value={value}
      onChange={onChange}
      onKeyDown={e => handleDiscPercentKeyDown(e, index, onAdd)}
      type="number"
      inputProps={{ min: 0, max: 100, step: 0.01 }}
      disabled={disabled}
      error={error}
      helperText={helperText}
    />
  );
}

function TotalsBar({ discAmt, rowTotal }) {
  return (
    <Box sx={{
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      gap: 3,
      mt: 1,
      pt: 1.25,
      borderTop: `1px dashed ${IPD_COLORS.border}`,
    }}>
      <Box sx={{ textAlign: 'right' }}>
        <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 11 }}>Disc Amt</Typography>
        <Typography sx={{ fontFamily: 'monospace', fontSize: 14, fontWeight: 600 }}>
          {formatCurrency(discAmt)}
        </Typography>
      </Box>
      <Box sx={{ textAlign: 'right' }}>
        <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 11 }}>Line Total</Typography>
        <Typography sx={{
          fontFamily: 'monospace',
          fontSize: 15,
          fontWeight: 700,
          color: IPD_COLORS.primary,
        }}>
          {formatCurrency(rowTotal)}
        </Typography>
      </Box>
    </Box>
  );
}

function DesktopLineCard({
  line, index, isDirect, uomLabel, uomOptions, poItemOptions, errors, canRemove,
  onRemove, onAdd, onUpdate, onPoItemSelect,
  receivedInt, pendingInt, qtyOverPending, rowTotal, discAmt,
  qtyErr, rateErr, itemErr, batchErr, expiryErr, freeQtyErr,
  mrpErr, saleErr, rateOverrideErr, todayDate, minExpiryDate, canOverridePricing,
  receivingStoreId, issueUomLabel, baseUomLabel, packErr, packReasonErr,
  handleField,
}) {
  const showRateOverride = !isDirect && isPoRateOverridden(line);
  const poRateHint = !isDirect && line.po_unit_rate
    ? `PO rate: ${line.po_unit_rate}`
    : undefined;
  const taxLocked = !isDirect && line.po_tax_percent !== '' && !canOverridePricing;
  const discLocked = !isDirect && line.po_discount_percent !== '' && !canOverridePricing;
  return (
    <Box
      data-grn-line={index + 1}
      sx={{
      display: 'grid',
      gridTemplateColumns: '36px 1fr 36px',
      gap: 1,
      mb: 2,
      p: 1.5,
      ...cardSx,
      backgroundColor: qtyOverPending ? `${IPD_COLORS.warning.text}0d` : undefined,
      borderColor: qtyOverPending ? IPD_COLORS.warning.text : IPD_COLORS.border,
    }}>
      <Box sx={actionColSx}>
        <DeleteLineButton canRemove={canRemove} onRemove={onRemove} index={index} />
      </Box>

      <Box sx={grid12Sx}>
        {/* Row 1 */}
        <Box sx={{ gridColumn: 'span 4' }} data-grn-focus="item">
          <FieldLabel required>Item</FieldLabel>
          {isDirect ? (
            <InvItemAutocomplete
              value={line.item_id}
              storeId={receivingStoreId}
              onChange={(itemId, item) => onUpdate(index, 'item_id', itemId, item)}
            />
          ) : (
            <PoItemAutocomplete
              value={line.po_item_id || line.purchase_order_item_id}
              options={poItemOptions}
              onChange={poItem => onPoItemSelect(index, poItem)}
            />
          )}
          {itemErr ? (
            <Typography sx={{ color: IPD_COLORS.error.text, fontSize: 11, mt: 0.5 }}>
              {errors.item_id}
            </Typography>
          ) : null}
        </Box>

        <Box sx={isDirect ? { gridColumn: 'span 2' } : undefined}>
          <FieldLabel>Purchase UOM</FieldLabel>
          <UomCell
            isDirect={isDirect}
            uomId={line.uom_id}
            uomLabel={uomLabel}
            uomOptions={uomOptions}
            onChange={val => onUpdate(index, 'uom_id', val)}
          />
        </Box>

        {!isDirect && (
          <Box>
            <FieldLabel>Ordered</FieldLabel>
            <ReadOnlyValue
              value={line.ordered_quantity ? formatQty(line.ordered_quantity) : '—'}
              align="right"
              mono
            />
          </Box>
        )}

        {!isDirect && (
          <Box>
            <FieldLabel>Pending</FieldLabel>
            <ReadOnlyValue
              value={pendingInt != null ? formatQty(pendingInt) : '—'}
              align="right"
              warn={pendingInt === 0}
              mono
            />
          </Box>
        )}

        <Box sx={isDirect ? { gridColumn: 'span 2' } : undefined}>
          <FieldLabel required>Received</FieldLabel>
          <InvTextField
            value={line.received_quantity}
            onChange={e => handleField('received_quantity', e.target.value)}
            type="number"
            inputProps={{
              min: 1,
              step: 1,
              inputMode: 'numeric',
              ...(pendingInt != null ? { max: pendingInt } : {}),
            }}
            error={Boolean(qtyErr)}
            helperText={qtyErr || undefined}
          />
        </Box>

        <Box sx={isDirect ? { gridColumn: 'span 2' } : undefined}>
          <FieldLabel>Free Qty</FieldLabel>
          <InvTextField
            value={line.free_quantity ?? '0'}
            onChange={e => handleField('free_quantity', e.target.value)}
            type="number"
            inputProps={{ min: 0, step: 1, inputMode: 'numeric' }}
            error={Boolean(freeQtyErr)}
            helperText={freeQtyErr || undefined}
          />
        </Box>

        <Box sx={{ gridColumn: isDirect ? 'span 2' : 'span 3' }}>
          <FieldLabel required>Purchase Rate</FieldLabel>
          <InvTextField
            value={line.unit_rate}
            onChange={e => handleField('unit_rate', e.target.value)}
            type="number"
            inputProps={{ min: 0.01, step: 0.01 }}
            error={Boolean(rateErr)}
            helperText={rateErr || poRateHint || undefined}
          />
        </Box>

        {showRateOverride ? (
          <Box sx={{ gridColumn: 'span 4' }}>
            <FieldLabel required>Rate Override Reason</FieldLabel>
            <InvTextField
              value={line.rate_override_reason || ''}
              onChange={e => handleField('rate_override_reason', e.target.value)}
              error={Boolean(rateOverrideErr)}
              helperText={rateOverrideErr || 'Required when rate differs from PO'}
            />
          </Box>
        ) : null}

        {/* Row 2 — batch & pricing */}
        <Box sx={{ gridColumn: 'span 2' }}>
          <FieldLabel required={line.is_batch_tracked}>Batch </FieldLabel>
          <InvTextField
            value={line.batch_number}
            onChange={e => handleField('batch_number', e.target.value)}
            error={Boolean(batchErr)}
            helperText={batchErr || undefined}
          />
        </Box>

        <Box sx={{ gridColumn: 'span 2' }}>
          {/*
            SILENT CALENDAR BUG: min/max on the popup do not stop clear+keyboard
            entry. Values are normalized to yyyy-MM-dd; validateLineDates shows
            the field error. See IpdDateField.js.
          */}
          <FieldLabel required={line.is_expiry_tracked}>Expiry Date</FieldLabel>
          <InvTextField
            value={line.expiry_date || ''}
            type="date"
            onChange={e => handleField('expiry_date', e.target.value)}
            inputProps={{ min: line.allow_expired_override ? undefined : minExpiryDate }}
            error={Boolean(expiryErr)}
            helperText={expiryErr || undefined}
          />
          <ExpiredOverrideControl
            line={line}
            todayDate={todayDate}
            onToggle={checked => handleField('allow_expired_override', checked)}
          />
        </Box>

        <PackSizeCell
          line={line}
          uomLabel={uomLabel}
          issueUomLabel={issueUomLabel}
          baseUomLabel={baseUomLabel}
          packErr={packErr}
          packReasonErr={packReasonErr}
          handleField={handleField}
          gridColumn="span 4"
        />

        <Box>
          <FieldLabel required>MRP</FieldLabel>
          <InvTextField
            value={line.mrp || ''}
            onChange={e => handleField('mrp', e.target.value)}
            type="number"
            inputProps={{ min: 0, step: 0.01 }}
            error={Boolean(mrpErr)}
            helperText={mrpErr || undefined}
          />
        </Box>

        <Box>
          <FieldLabel>Sale Rate</FieldLabel>
          <InvTextField
            value={line.sale_rate || ''}
            onChange={e => handleField('sale_rate', e.target.value)}
            type="number"
            inputProps={{
              min: line.unit_rate || 0,
              step: 0.01,
              ...(line.mrp ? { max: line.mrp } : {}),
            }}
            error={Boolean(saleErr)}
            helperText={saleErr || (!line.sale_rate ? 'Defaults to MRP when blank' : undefined)}
          />
        </Box>

        <Box>
          <FieldLabel>Tax %</FieldLabel>
          <InvTextField
            value={line.tax_percent ?? '0'}
            onChange={e => handleField('tax_percent', e.target.value)}
            type="number"
            inputProps={{ min: 0, max: 28, step: 0.01 }}
            disabled={taxLocked}
            error={Boolean(errors?.tax_percent)}
            helperText={errors?.tax_percent || (taxLocked ? 'From PO' : undefined)}
          />
        </Box>

        <Box>
          <FieldLabel>Disc %</FieldLabel>
          <DiscPercentField
            value={line.discount_percent ?? '0'}
            onChange={e => handleField('discount_percent', e.target.value)}
            onAdd={onAdd}
            index={index}
            disabled={discLocked}
            error={Boolean(errors?.discount_percent)}
            helperText={errors?.discount_percent || (discLocked ? 'From PO' : undefined)}
          />
        </Box>

        {/* Totals — row 3 */}
        <Box sx={{ gridColumn: '9 / span 2' }}>
          <FieldLabel>Disc Amt</FieldLabel>
          <ReadOnlyValue value={formatCurrency(discAmt)} align="right" mono />
        </Box>

        <Box sx={{ gridColumn: '11 / span 2' }}>
          <FieldLabel>Line Total</FieldLabel>
          <ReadOnlyValue value={formatCurrency(rowTotal)} align="right" mono />
        </Box>
      </Box>

      <Box sx={actionColSx}>
        <AddLineButton onAdd={onAdd} index={index} />
      </Box>
    </Box>
  );
}

function MobileLineCard({
  line, index, isDirect, uomLabel, uomOptions, poItemOptions, errors, canRemove,
  onRemove, onAdd, onUpdate, onPoItemSelect,
  receivedInt, pendingInt, qtyOverPending, rowTotal, discAmt,
  qtyErr, rateErr, itemErr, batchErr, expiryErr, freeQtyErr,
  mrpErr, saleErr, rateOverrideErr, todayDate, minExpiryDate, canOverridePricing,
  receivingStoreId, issueUomLabel, baseUomLabel, packErr, packReasonErr,
  handleField,
}) {
  const showRateOverride = !isDirect && isPoRateOverridden(line);
  const poRateHint = !isDirect && line.po_unit_rate
    ? `PO rate: ${line.po_unit_rate}`
    : undefined;
  const taxLocked = !isDirect && line.po_tax_percent !== '' && !canOverridePricing;
  const discLocked = !isDirect && line.po_discount_percent !== '' && !canOverridePricing;
  const twoCol = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 };
  const threeCol = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1.5 };

  return (
    <Box
      data-grn-line={index + 1}
      sx={{
      mb: 2,
      p: 2,
      ...cardSx,
      backgroundColor: qtyOverPending ? `${IPD_COLORS.warning.text}0d` : undefined,
      borderColor: qtyOverPending ? IPD_COLORS.warning.text : IPD_COLORS.border,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteLineButton canRemove={canRemove} onRemove={onRemove} index={index} />
          <Typography sx={{ fontWeight: 600, fontSize: 13, color: IPD_COLORS.textPrimary }}>
            Line {index + 1}
          </Typography>
        </Box>
        <AddLineButton onAdd={onAdd} index={index} />
      </Box>

      <SectionTitle>Item &amp; Quantities</SectionTitle>
      <Box sx={{ mb: 1.5 }} data-grn-focus="item">
        <FieldLabel required>Item</FieldLabel>
        {isDirect ? (
          <InvItemAutocomplete
            value={line.item_id}
            storeId={receivingStoreId}
            onChange={(itemId, item) => onUpdate(index, 'item_id', itemId, item)}
          />
        ) : (
          <PoItemAutocomplete
            value={line.po_item_id || line.purchase_order_item_id}
            options={poItemOptions}
            onChange={poItem => onPoItemSelect(index, poItem)}
          />
        )}
        {itemErr ? (
          <Typography sx={{ color: IPD_COLORS.error.text, fontSize: 11, mt: 0.5 }}>
            {errors.item_id}
          </Typography>
        ) : null}
      </Box>

      <Box sx={{ ...threeCol, mb: 1.5 }}>
        <Box>
          <FieldLabel>Purchase UOM</FieldLabel>
          <UomCell
            isDirect={isDirect}
            uomId={line.uom_id}
            uomLabel={uomLabel}
            uomOptions={uomOptions}
            onChange={val => onUpdate(index, 'uom_id', val)}
          />
        </Box>
        {!isDirect && (
          <Box>
            <FieldLabel>Ordered</FieldLabel>
            <ReadOnlyValue
              value={line.ordered_quantity ? formatQty(line.ordered_quantity) : '—'}
              align="right"
              mono
            />
          </Box>
        )}
        {!isDirect && (
          <Box>
            <FieldLabel>Pending</FieldLabel>
            <ReadOnlyValue
              value={pendingInt != null ? formatQty(pendingInt) : '—'}
              align="right"
              warn={pendingInt === 0}
              mono
            />
          </Box>
        )}
      </Box>

      <Box sx={{ ...threeCol, mb: 1.5 }}>
        <Box>
          <FieldLabel required>Received</FieldLabel>
          <InvTextField
            value={line.received_quantity}
            onChange={e => handleField('received_quantity', e.target.value)}
            type="number"
            inputProps={{
              min: 1,
              step: 1,
              inputMode: 'numeric',
              ...(pendingInt != null ? { max: pendingInt } : {}),
            }}
            error={Boolean(qtyErr)}
            helperText={qtyErr || undefined}
          />
        </Box>
        <Box>
          <FieldLabel>Free Qty</FieldLabel>
          <InvTextField
            value={line.free_quantity ?? '0'}
            onChange={e => handleField('free_quantity', e.target.value)}
            type="number"
            inputProps={{ min: 0, step: 1, inputMode: 'numeric' }}
            error={Boolean(freeQtyErr)}
            helperText={freeQtyErr || undefined}
          />
        </Box>
        <Box>
          <FieldLabel required>Rate</FieldLabel>
          <InvTextField
            value={line.unit_rate}
            onChange={e => handleField('unit_rate', e.target.value)}
            type="number"
            inputProps={{ min: 0.01, step: 0.01 }}
            error={Boolean(rateErr)}
            helperText={rateErr || poRateHint || undefined}
          />
        </Box>
      </Box>

      {showRateOverride ? (
        <Box sx={{ mb: 1.5 }}>
          <FieldLabel required>Rate Override Reason</FieldLabel>
          <InvTextField
            value={line.rate_override_reason || ''}
            onChange={e => handleField('rate_override_reason', e.target.value)}
            error={Boolean(rateOverrideErr)}
            helperText={rateOverrideErr || 'Required when rate differs from PO'}
          />
        </Box>
      ) : null}

      <Divider sx={{ my: 1.5, borderColor: IPD_COLORS.border }} />
      <SectionTitle>Batch &amp; Dates</SectionTitle>
      <Box sx={{ ...twoCol, mb: 1.5 }}>
        <Box>
          <FieldLabel required={line.is_batch_tracked}>Batch </FieldLabel>
          <InvTextField
            value={line.batch_number}
            onChange={e => handleField('batch_number', e.target.value)}
            error={Boolean(batchErr)}
            helperText={batchErr || undefined}
          />
        </Box>
        <Box>
          {/* SILENT CALENDAR BUG — see the comment in the wide layout above / IpdDateField.js */}
          <FieldLabel required={line.is_expiry_tracked}>Expiry Date</FieldLabel>
          <InvTextField
            value={line.expiry_date || ''}
            type="date"
            onChange={e => handleField('expiry_date', e.target.value)}
            inputProps={{ min: line.allow_expired_override ? undefined : minExpiryDate }}
            error={Boolean(expiryErr)}
            helperText={expiryErr || undefined}
          />
          <ExpiredOverrideControl
            line={line}
            todayDate={todayDate}
            onToggle={checked => handleField('allow_expired_override', checked)}
          />
        </Box>
        <PackSizeCell
          line={line}
          uomLabel={uomLabel}
          issueUomLabel={issueUomLabel}
          baseUomLabel={baseUomLabel}
          packErr={packErr}
          packReasonErr={packReasonErr}
          handleField={handleField}
          gridColumn="1 / -1"
        />
      </Box>

      <Divider sx={{ my: 1.5, borderColor: IPD_COLORS.border }} />
      <SectionTitle>Pricing</SectionTitle>
      <Box sx={{ ...twoCol, mb: 1 }}>
        <Box>
          <FieldLabel required>MRP</FieldLabel>
          <InvTextField
            value={line.mrp || ''}
            onChange={e => handleField('mrp', e.target.value)}
            type="number"
            inputProps={{ min: 0, step: 0.01 }}
            error={Boolean(mrpErr)}
            helperText={mrpErr || undefined}
          />
        </Box>
        <Box>
          <FieldLabel>Sale Rate</FieldLabel>
          <InvTextField
            value={line.sale_rate || ''}
            onChange={e => handleField('sale_rate', e.target.value)}
            type="number"
            inputProps={{
              min: line.unit_rate || 0,
              step: 0.01,
              ...(line.mrp ? { max: line.mrp } : {}),
            }}
            error={Boolean(saleErr)}
            helperText={saleErr || (!line.sale_rate ? 'Defaults to MRP when blank' : undefined)}
          />
        </Box>
        <Box>
          <FieldLabel>Tax %</FieldLabel>
          <InvTextField
            value={line.tax_percent ?? '0'}
            onChange={e => handleField('tax_percent', e.target.value)}
            type="number"
            inputProps={{ min: 0, max: 28, step: 0.01 }}
            disabled={taxLocked}
            error={Boolean(errors?.tax_percent)}
            helperText={errors?.tax_percent || (taxLocked ? 'From PO' : undefined)}
          />
        </Box>
        <Box>
          <FieldLabel>Disc %</FieldLabel>
          <DiscPercentField
            value={line.discount_percent ?? '0'}
            onChange={e => handleField('discount_percent', e.target.value)}
            onAdd={onAdd}
            index={index}
            disabled={discLocked}
            error={Boolean(errors?.discount_percent)}
            helperText={errors?.discount_percent || (discLocked ? 'From PO' : undefined)}
          />
        </Box>
      </Box>

      <TotalsBar discAmt={discAmt} rowTotal={rowTotal} />
    </Box>
  );
}

function GrnLineCard(props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (isMobile) {
    return <MobileLineCard {...props} />;
  }
  return <DesktopLineCard {...props} />;
}

export default function GrnLineItemsEditor({
  lines,
  isDirect,
  uomMap = {},
  uomOptions = [],
  /** { optionsFor(itemId, currentUomId), factorFor(...) } — restricts each
   *  Direct-GRN line's purchase-UOM picker to units that reach the item's base
   *  unit. Omitted, or before its rules load, the picker shows the full list. */
  uomPolicy,
  getPoItemOptions,
  getLineErrors,
  showValidationErrors = false,
  canRemoveLine,
  onAddLine,
  onRemoveLine,
  onUpdateLine,
  onPoItemSelect,
  todayDate,
  canOverridePricing = false,
  receivingStoreId,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (!lines.length) {
    return (
      <Typography sx={{ color: IPD_COLORS.textSecondary, textAlign: 'center', py: 4 }}>
        No line items — click &quot;Add Row&quot; to begin
      </Typography>
    );
  }

  return (
    <Box sx={{
      ...(!isMobile ? { overflowX: 'auto', pb: 0.5 } : null),
    }}>
      <Box sx={!isMobile ? { minWidth: 960 } : undefined}>
        {lines.map((line, i) => {
          const uomId = line.uom_id;
          const uomLabel = line.uom_name || line.uom_code || (uomId ? uomMap[uomId] : '') || '';
          const rowUomOptions = isDirect && uomPolicy
            ? uomPolicy.optionsFor(line.item_id, uomId)
            : uomOptions;
          const receivedInt = parseInt(line.received_quantity, 10) || 0;
          const pendingInt = line.pending_quantity !== '' && line.pending_quantity != null
            ? Math.floor(Number(line.pending_quantity) || 0)
            : null;
          const qtyOverPending = !isDirect && pendingInt != null && receivedInt > pendingInt
            && line.received_quantity !== '';
          const errors = getLineErrors ? getLineErrors(line, i) : {};
          const qtyErr = line.received_quantity && errors?.received_quantity;
          const rateErr = line.unit_rate !== '' && errors?.unit_rate;
          const itemErr = !isDirect && errors?.item_id
            && (line.po_item_id || line.purchase_order_item_id || line.received_quantity || line.unit_rate);
          const batchErr = (line.is_batch_tracked || errors?.batch_number) && errors?.batch_number;
          const expiryErr = (line.is_expiry_tracked || errors?.expiry_date) && errors?.expiry_date;
          const freeQtyErr = line.free_quantity !== '' && errors?.free_quantity;
          const lineStarted = isDirect
            ? Boolean(line.item_id)
            : Boolean(line.po_item_id || line.purchase_order_item_id);
          const mrpErr = showValidationErrors && lineStarted && errors?.mrp;
          // Show sale-rate errors as soon as the user enters a bad value — do not
          // wait for Next (which stays disabled while lines are invalid).
          const hasSaleInput = line.sale_rate !== '' && line.sale_rate != null;
          const saleErr = lineStarted && errors?.sale_rate && (
            showValidationErrors || hasSaleInput
          );
          const rateOverrideErr = errors?.rate_override_reason;
          const packErr = errors?.pack_to_issue;
          const packReasonErr = errors?.override_reason;
          const baseUomLabel = line.base_uom_id ? (uomMap[line.base_uom_id] || '') : '';
          // The unit the pack size is typed against — the item's dispense unit,
          // which for all but sixteen items is the base unit itself.
          const issueUomLabel = line.issue_uom_id
            ? (uomMap[line.issue_uom_id] || '')
            : baseUomLabel;
          const minExpiryDate = minGrnExpiryDate(todayDate);
          const rowTotal = lineTotal(
            receivedInt,
            line.unit_rate,
            line.tax_percent,
            line.discount_percent,
          );
          const discAmt = computeLineDiscountAmount(
            receivedInt,
            line.unit_rate,
            line.discount_percent,
          );

          return (
            <GrnLineCard
              key={i}
              line={line}
              index={i}
              isDirect={isDirect}
              uomLabel={uomLabel}
              uomOptions={rowUomOptions}
              poItemOptions={getPoItemOptions ? getPoItemOptions(i) : []}
              errors={errors}
              canRemove={canRemoveLine}
              onRemove={onRemoveLine}
              onAdd={onAddLine}
              onUpdate={onUpdateLine}
              onPoItemSelect={onPoItemSelect}
              receivedInt={receivedInt}
              pendingInt={pendingInt}
              qtyOverPending={qtyOverPending}
              rowTotal={rowTotal}
              discAmt={discAmt}
              qtyErr={qtyErr}
              rateErr={rateErr}
              itemErr={itemErr}
              batchErr={batchErr}
              expiryErr={expiryErr}
              freeQtyErr={freeQtyErr}
              mrpErr={mrpErr}
              saleErr={saleErr}
              rateOverrideErr={rateOverrideErr}
              issueUomLabel={issueUomLabel}
              baseUomLabel={baseUomLabel}
              packErr={packErr}
              packReasonErr={packReasonErr}
              todayDate={todayDate}
              minExpiryDate={minExpiryDate}
              canOverridePricing={canOverridePricing}
              receivingStoreId={receivingStoreId}
              handleField={(key, val) => {
                onUpdateLine(i, key, val);
                if (key === 'expiry_date' && val && val > todayDate && line.allow_expired_override) {
                  onUpdateLine(i, 'allow_expired_override', false);
                }
              }}
            />
          );
        })}
      </Box>
    </Box>
  );
}
